import React, { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { Divider, Paper, Stack } from '@mui/material'
import { Container, Col, Row, ListGroup } from 'react-bootstrap'
import Slider from "react-slick"
import moment from 'moment'
import { FaShieldAlt } from "react-icons/fa"
import { TbArrowsMinimize, TbArrowsMaximize, TbCertificate } from "react-icons/tb"
import { IoListOutline } from "react-icons/io5"

import Floorplan from './components/FloorplanDisplay'
import AlertIcons from '../components/AlertIcons'

import DataTable from 'Shared/Components/DataTable'
import PopupModal from 'Shared/Components/PopupModal'
import useFetchData from 'Shared/Hooks/useFetchData'
import { apiUrl } from 'Shared/utils'
import useLoop from 'Shared/Hooks/useLoop'
import { IconButtonComponent, IconComponent } from 'Shared/Components/Icon'

function SliderArrow(props) {
  const { className, style, onClick } = props
  
  return (
    <div
      className={className}
      style={{ ...style, display: "block" }}
      onClick={onClick}
    />
  )
}

const SmartwatchConfinedSpace = () => {
  const { role, setPageLoading } = useOutletContext()
  const navigate = useNavigate()

  const { t } = useTranslation()
  const { send } = useFetchData()

  const [data, setData] = useState([])
  const [minimize, setMinimize] = useState(false)
  const [floorplanListOpen, setFloorplanListOpen] = useState(false)
  const [floorplanList, setFloorplanList] = useState([])

  const [locationInfo, setLocationInfo] = useState()

  const mapRef = useRef()
  const tableRef = useRef()
  const filterRef = useRef({
    "search": ""
  })

  const loadData = async () => {
    const data = await send({
      method: "GET",
      url: `${apiUrl}/smartwatch/latest`,
      params: {
        "search": filterRef.current.search,
        "type": "confined_space",
      },
      returnType: "json"
    })

    const zones = await send({
      method: "GET",
      url: `${apiUrl}/smartwatch/zones`,
      returnType: "json"
    })

    setLocationInfo(data["locations"])

    setData(data["data"].map((i) => ({
      "online": (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", textAlign: "center" }}>
          <span style={{ height: "15px", width: "15px", backgroundColor: i["online"] ? "green" : "red", borderRadius: "50%" }}></span>
        </div>
      ),
      "online_status": i["online"],
      "in_tunnel": i["in_tunnel"],
      "device_id": i["device_id"],
      "worker_id": i["worker_id"],
      "worker_name": <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
        <span>{i["worker_name"]}</span>
        <span>{i["standby_person"] && <IconComponent title="Standby Person"><FaShieldAlt /></IconComponent>}</span>
      </div>,
      "battery_level": i["battery_level"],
      "body_temp": i["body_temp"] != null ? <div className={i["body_temp_alert"] ? 'text-alert' : "text-normal"}>{(role === 'pm' || role === 'admin') ? i["body_temp"] : t(`smartwatch_bodyTemp_${i["body_temp"]}`)}</div> : null,
      "heart_rate": i["heart_rate"] != null ? <div className={i["heart_rate_alert"] ? 'text-alert' : "text-normal"}>{(role === 'pm' || role === 'admin') ? i["heart_rate"] : t(`smartwatch_heartRate_${i["heart_rate"]}`)}</div> : null,
      "zone_id": i["zone"],
      "zone_name": i["zone_name"],
      "sos": i["sos"],
      "alerts": i["online"] ? <AlertIcons data={i["alerts"]} /> : null,
      "last_update": i["last_update"] != null ? moment(i["last_update"]).format("YYYY-MM-DD HH:mm:ss") : null
    })))

    if (mapRef.current) {
      mapRef.current.zoneUpdate(zones.map((i) => ({
        "zone_id": i["zone_id"],
        "zone_name": i["zone_name"],
        "zone_description": i["zone_description"],
        "marker_lon": i["marker_lon"],
        "marker_lat": i["marker_lat"],
        "workers": data["data"].filter((j) => j["zone"] === i["zone_id"])
      })))
    }
  }

  const fetchFloorPlan = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/smartwatch/floorplan_meta`,
      returnType: "json"
    })

    body.forEach(async (i) => {
      const blob = await send({
        method: "GET",
        url: `${apiUrl}/smartwatch/floorplan`,
        params: {
          "location": i["location"]
        },
        returnType: "blob"
      })

      mapRef.current.addFloorPlan(blob, i)
    })

    setFloorplanList(body)
  }

  const fetchMapExtent = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/smartwatch/map_extent`,
      returnType: "json"
    })

    if (body != null) {
      mapRef.current.setMapExtent(body)
    }
  }
  
  useEffect(() => {
    const init = async () => {
      setPageLoading(true)

      await Promise.all([
        loadData(),
        fetchFloorPlan(),
        fetchMapExtent()
      ])

      setPageLoading(false)
    }

    init()    
  }, [])

  useLoop(loadData, 10000)

  const translate_permit_status = (status) => {
    switch (status) {
      case -100:
        return "Missing"
      case -101:
        return "Expired"
      case 102:
        return "Voided"
      case 0:
        return "Pending"
      case 100:
        return "Issued"
      case 101:
        return "Cancelled"
      default:
        return "-"
    }
  }

  return (<>
    <div className="mainContainer" style={{ backgroundColor: "white", borderRadius: "15px" }}>
      <Container fluid className="mainContainer" style={{ flex: 1, backgroundColor: "white", borderRadius: "15px" }}>
        <Row style={{ height: minimize ? "100%" : "40%" }}>
          <Col xs={12} style={{ height: "100%", overflow: "hidden", padding: "5px 10px 5px 10px", display: "flex" }}>
            <div style={{ flex: 1 }}>
              <Floorplan ref={mapRef}/>
            </div>

            {!minimize && 
              <div style={{ height: "100%", display: "flex", flexFlow: "column", justifyContent: "center", alignItems: "center" }}>
                <div style={{ width: "180px", display: "grid", gap: "10px", paddingLeft: "10px", paddingRight: "5px", overflow: "auto" }}>
                  <div style={{ display: "flex", width: "100%", height: "100px", flexFlow: "column", border: "2px solid black" }}>
                    <div style={{ textAlign: "center", borderBottom: "2px solid black" }}>
                      <div style={{ padding: "5px" }}>Online</div>
                    </div>

                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", textAlign: "center", flexGrow: 1 }}>
                      <div style={{ display: "grid", gridTemplateColumns: "120px 10px auto" }}>
                        <div style={{ display: "flex" }}><span style={{ flex: 1, textAlign: "center" }}>{t("module_confinedSpace")}</span>:</div>
                        <div></div>
                        <div>{data.filter((i) => i["online_status"] && i["in_tunnel"]).length}</div>

                        <div style={{ display: "flex" }}><span style={{ flex: 1, textAlign: "center" }}>Ground</span>:</div>
                        <div></div>
                        <div>{data.filter((i) => i["online_status"] && !i["in_tunnel"]).length}</div>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: "flex", width: "100%", height: "100px", flexFlow: "column", border: "2px solid black" }}>
                    <div style={{ textAlign: "center", borderBottom: "2px solid black" }}>
                      <div style={{ padding: "5px" }}>Offline</div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", textAlign: "center", flexGrow: 1 }}>
                      <div>{data.filter((i) => !i["online_status"]).length}</div>
                    </div>
                  </div>
                </div>
              </div>
            }
          </Col>
        </Row>

        {minimize ? (
          <Row style={{ display: minimize ? "block" : "none" }}>
            <Col xs={12} style={{ height: "100%", padding: "5px 10px 10px 10px" }}>
              <div style={{ width: "100%", display: "flex", justifyContent: "end", paddingRight: "10px" }}>
                <IconButtonComponent text={t("smartwatch_floorplan")} onClick={() => setFloorplanListOpen(true)}>
                  <IoListOutline size={20}/>
                </IconButtonComponent>
                
                <IconButtonComponent text={t("general_maximize")} onClick={() => setMinimize(false)}>
                  <TbArrowsMaximize size={20}/>
                </IconButtonComponent>
              </div>
            </Col>
          </Row>
        ) : (
          <Row style={{ height: "60%", display: minimize ? "none" : "block" }}>
            <Col xs={12} style={{ display: "flex", flexFlow: "column", height: "100%", padding: "5px 10px 10px 10px" }}>
              <div style={{ flex: 1, display: "flex", flexFlow: "column", overflow: "hidden" }}>
                {locationInfo && 
                  <Slider style={{ height: (locationInfo ?? []).filter((i) => i["status"]).length > 0 ? "120px" : "70px" }}
                    dots={true} infinite={false} speed={500} slidesToShow={2} slidesToScroll={1}
                    nextArrow={<SliderArrow />} prevArrow={<SliderArrow />}
                    responsive={[{ breakpoint: 1200, settings: { slidesToShow: 1 } }]}
                  >
                      {(locationInfo).map((i) => 
                        <div>
                          <div style={{ border: "2px solid grey", borderRadius: "15px", height: locationInfo.filter((i) => i["status"]).length > 0 ? "120px" : "70px" }}>
                            <div style={{ height: "100%", display: "flex", flexFlow: "column", borderRadius: "10px", padding: "5px" }}>
                              <div>{i["location_name"]}</div>

                              <Divider sx={{ bgcolor: "secondary.light" }}/>

                              <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
                                {i["status"] ? (<div>
                                  <div><b>{t("module_confinedSpace_workInProgress")}</b></div>

                                  <div style={{ display: "flex", gap: "10px" }}>
                                    <span><IconComponent title="Standby Person"><FaShieldAlt /></IconComponent></span>
                                    <span>{i["standby_person"]["list"] ? i["standby_person"]["list"] : (i["standby_person"]["last_presence"] ? `N/A (Last Presence: ${i["standby_person"]["last_presence"]}) ` : "N/A")}</span>
                                  </div>

                                  <div style={{ display: "flex", gap: "10px" }}>
                                    <span><IconComponent title="Working Permit"><TbCertificate /></IconComponent></span>
                                    <span>{i["working_permit"] ? <span className={i["working_permit"]["status"] > 0 ? 'text-normal' : 'text-alert'} style={{ cursor: "pointer" }} onClick={() => navigate(`../../permit_to_work/list/${i["working_permit"]["template_id"]}/${i["working_permit"]["form_id"]}`)}><u>{translate_permit_status(i["working_permit"]["status"])}</u></span> : "N/A"}</span>
                                  </div>
                                </div>) : (
                                  <div><b>{t("module_confinedSpace_NoworkInProgress")}</b></div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                  </Slider> 
                }

                <div style={{ flex: 1, display: "flex", flexFlow: "column", overflow: "hidden" }}>
                  <Paper elevation={0} sx={{ display: "flex", flexFlow: "column", height: "100%" }}>
                    <DataTable
                      data={data} count={null} ref={tableRef}
                      columns={{
                        "online": { "label": t("general_online"), "sorting": false, "nowrap": true },
                        "worker_name": { "label": t("smartwatch_worker"), "sorting": false, "nowrap": true },
                        "battery_level": { "label": t("smartwatch_betteryLevel"), "sorting": false, "nowrap": true },
                        "body_temp": { "label": t("smartwatch_bodyTemp"), "sorting": false, "nowrap": true },
                        "heart_rate": { "label": t("smartwatch_heartRate"), "sorting": false, "nowrap": true },
                        "zone_name": { "label": t("smartwatch_zone"), "sorting": false, "nowrap": true },
                        "alerts": { "label": t("smartwatch_alerts"), "sorting": false, "nowrap": true },
                        "last_update": { "label": t("general_lastUpdate"), "sorting": false, "nowrap": true } 
                      }}
                      appActions={() => { return [
                        {icon: <IoListOutline size={20}/>, text: t("smartwatch_floorplan"), onClick: function() { setFloorplanListOpen(true) }},
                        {icon: <TbArrowsMinimize size={20}/>, text: t("general_minimize"), onClick: function() { setMinimize(true) }},
                      ]}}
                    />
                  </Paper>
                </div>
              </div>
            </Col>
          </Row>
        )}
      </Container>
    </div>

    <PopupModal isOpen={floorplanListOpen} width="50%" maxHeight="50%" close toggle={() => setFloorplanListOpen(false)}>
      <ListGroup variant="flush">
        {(floorplanList ?? []).map((i) => 
          <ListGroup.Item className="border-0" action onClick={() => { mapRef.current.focus(i); setFloorplanListOpen(false); }}>
            {`${i["location_name"]}`}
          </ListGroup.Item>
        )}
      </ListGroup>
    </PopupModal>
  </>)
}

export default SmartwatchConfinedSpace
